import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, HostBinding, Inject, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { EDITOR_INLINE_EDIT } from '../editor-config';
import { DashboardService } from '../../dashboard.service';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export interface DialogData {
  _id: string;
  templateName: string;
  templateType: string;
  content: any;
  heading: string;
  update: boolean;
  subject: string;
  templatePlaceholder: string;
  difficultyLevel?: string;
}

export interface Tag {
  name: string;
}

@Component({
  selector: 'app-create-update-mcq',
  templateUrl: './create-update-mcq.component.html',
  styleUrls: ['./create-update-mcq.component.scss']
})

export class CreateUpdateMcqComponent implements OnInit {

  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  tags: Tag[] = [];

  @Output() createUpdateMcq = new EventEmitter<string>();

  createMcqFrom: UntypedFormGroup;
  difficultyLevels = [
    { id: 'LOW', name: 'Easy' },
    { id: 'MEDIUM', name: 'Medium' },
    { id: 'HIGH', name: 'Hard' }
  ];
  minutes: number[] = [];
  editorConfig: AngularEditorConfig = EDITOR_INLINE_EDIT;

  optionEditorConfig: AngularEditorConfig = EDITOR_INLINE_EDIT;
  correctOptionIdx: number = 0;
  topics = [];
  private topicsPageNo: number = 1;
  private totalTopics: number = 0;
  private topicsSearchTerm: string = '';
  topicsSettings = {
    singleSelection: false,
    idField: '_id',
    textField: 'topic',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
    // Keep search box visible even when data list is empty (remote search)
    allowRemoteDataSearch: true
  };

  subTopicsSettings = {
    singleSelection: false,
    idField: '_id',
    textField: 'name',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
    // Keep search box visible even when data list is empty (remote search)
    allowRemoteDataSearch: true
  };
  subTopics = [];
  private subTopicsPageNo: number = 1;
  private totalSubTopics: number = 0;
  private searchTerm: string = '';
  private limit: number = 50;
  sourceSubTopics = [];

  private filterSubject = new Subject<string>();
  private topicFilterSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  private subscriptions: Subscription[] = [];

  constructor(public dialogRef: MatDialogRef<CreateUpdateMcqComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private fb: UntypedFormBuilder,
    private dashboardService: DashboardService) {
    this.createMcqFrom = this.fb.group({
      hourLimit: [0],
      minLimit: [2, [Validators.required]],
      questionType: ['MULTIPLE_CHOICE', [Validators.required]],
      questionText: ['', [Validators.required]],
      answer: [''],
      enterpriseOptions: this.fb.array(this.initialOptions()),
      selectedTopics: [[], [Validators.required]],
      difficultyLevel: [this.data?.difficultyLevel || '', [Validators.required]],
      subTopics: [[]],
      shuffle: true
    });
  }

  /**
   * Utility method to deduplicate topics based on _id
   * @param topics Array of topic objects
   * @returns Deduplicated array of topics
   */
  private deduplicateTopics(topics: { _id: string, topic: string }[]): { _id: string, topic: string }[] {
    const seen = new Set<string>();
    return topics.filter(topic => {
      if (seen.has(topic._id)) {
        return false;
      }
      seen.add(topic._id);
      return true;
    });
  }

  ngOnInit(): void {
    this.getTopics('');
    this.minutes = [];
    for (let index = 1; index < 60; index++) {
      this.minutes.push(index);
    }

    // Ensure proper focus management
    setTimeout(() => {
      const firstInput = document.querySelector('.create-mcq-view input, .create-mcq-view textarea');
      if (firstInput) {
        (firstInput as HTMLElement).focus();
      }
    }, 100);
    this.filterSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe((searchTerm) => {
      this.getSubTopics(searchTerm);
    });
    this.topicFilterSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe((searchTerm) => {
      this.getTopics(searchTerm);
    });
  }

  getNextBatchSubTopics() {
    this.subTopicsPageNo++;
    if (this.totalSubTopics > this.subTopics?.length) {
      this.getSubTopics(this.searchTerm, 'nextBatch');
    }
  }

  getSubTopics(serchTerm, nextBatch = ''): void {
    const topicIds = this.createMcqFrom.value.selectedTopics.map(item => item._id).join(',');

    // Don't fetch sub-topics if no topics are selected
    if (!topicIds || topicIds.trim() === '') {
      this.subTopics = [];
      this.totalSubTopics = 0;
      return;
    }

    if (nextBatch !== 'nextBatch') {
      this.subTopicsPageNo = 1;
    }
    this.searchTerm = serchTerm;
    const reqObj = {
      pageNo: this.subTopicsPageNo,
      limit: this.limit,
      topicIds: topicIds.trim(),
      searchWord: this.searchTerm
    };
    this.subscriptions.push(
      this.dashboardService.getSubTopics(reqObj).subscribe((response: any) => {
        let res = response?.docs?.map(t => {
          t.name = `${t.name}  (${t.topic.topic})`
          return t;
        });
        if (nextBatch === 'nextBatch') {
          this.subTopics = [...this.subTopics, ...res];
        } else {
          this.subTopics = [...res];
        }
        this.totalSubTopics = response.total;
      })
    );
  }

  getNextBatchTopics() {
    this.topicsPageNo++;
    if (this.totalTopics > this.topics?.length) {
      this.getTopics(this.topicsSearchTerm, 'nextBatch');
    }
  }

  getTopics(searchTerm = '', nextBatch = ''): void {
    if (nextBatch !== 'nextBatch') {
      this.topicsPageNo = 1;
    }
    this.topicsSearchTerm = searchTerm;
    const params = { pageNo: this.topicsPageNo, limit: 20, searchWord: this.topicsSearchTerm };
    this.dashboardService.getEnterpriseTopics(params).subscribe((response: any) => {
      // Deduplicate API response topics
      const deduplicatedResponseTopics = this.deduplicateTopics(response.topics || []);

      if (nextBatch === 'nextBatch') {
        // Merge with existing topics and deduplicate
        const combinedTopics = [...this.topics, ...deduplicatedResponseTopics];
        this.topics = this.deduplicateTopics(combinedTopics);
      } else {
        this.topics = deduplicatedResponseTopics;
      }
      this.totalTopics = response.total;
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  get enterpriseOptions(): UntypedFormArray {
    return <UntypedFormArray>this.createMcqFrom.get('enterpriseOptions');
  }

  initialOptions() {
    return [new UntypedFormControl('', Validators.required), new UntypedFormControl('', Validators.required), new UntypedFormControl('', Validators.required), new UntypedFormControl('', Validators.required)]
  }

  addOption() {
    this.enterpriseOptions.push(new UntypedFormControl('', Validators.required));
  }

  removeOption(idx: number): void {
    // If we're removing the correct answer, reset to first option
    if (idx === this.correctOptionIdx) {
      this.correctOptionIdx = 0;
    } else if (idx < this.correctOptionIdx) {
      // If we're removing an option before the correct answer, adjust the index
      this.correctOptionIdx--;
    }

    this.enterpriseOptions.removeAt(idx);
  }

  addOrUpdateQuestion(): void {
    let topics = [];
    let subTopics = [];
    topics.push(...this.createMcqFrom.get('selectedTopics').value.map(topic => topic._id));
    subTopics.push(...this.createMcqFrom.value?.subTopics.map(i => i._id))
    const tempObj = {
      update: this.data?.update,
      _id: this.data?.content._id,
      topics: topics,
      subTopics: subTopics
    }
    delete this.createMcqFrom.value['selectedTopics'];
    const quesData = { ...this.createMcqFrom.value, ...tempObj };
    quesData.answerIndex = this.correctOptionIdx;
    this.createUpdateMcq.emit(quesData);
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.tags.push({ name: value });
    }
    event.chipInput!.clear();
  }

  removeTag(tag: Tag): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
    }
  }

  setCorrectOption(idx: number): void {
    this.correctOptionIdx = idx;
  }

  hasSelectedTopics(): boolean {
    const topics = this.createMcqFrom.value.selectedTopics || [];
    return topics.length > 0;
  }

  onTopicItemSelect(item): void {
    this.getSubTopics('');
  }

  onTopicDeselect(item): void {
    // If all topics are deselected, clear sub-topics
    if (!this.hasSelectedTopics()) {
      this.createMcqFrom.patchValue({ subTopics: [] });
      this.subTopics = [];
      this.totalSubTopics = 0;
    } else {
      // Refresh sub-topics for remaining topics
      this.getSubTopics('');
    }
  }

  onFilterChange(search: string) {
    this.filterSubject.next(search);
  }

  onTopicFilterChange(search: string) {
    this.topicFilterSubject.next(search);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }

}
